import { format } from "date-fns";
import { Handshake, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { PRIORITY_DOT } from "@/components/tasks/priority";
import { formatTimeInTz, getDefaultTimezone } from "@/lib/timezones";
import type { CalendarEvent } from "@/api/calendar";

interface EventPillProps {
  event: CalendarEvent;
  /** Compact pill (month view) vs richer card (week view). */
  variant?: "compact" | "card";
  /** Done predicate from useTaskStatuses — drives the strikethrough +
   *  opacity dimming so we never hardcode a status key. */
  isDone: (key: string | null | undefined) => boolean;
  onClick: (event: CalendarEvent) => void;
}

export function EventPill({
  event,
  variant = "compact",
  isDone,
  onClick,
}: EventPillProps) {
  const task = event.task;
  const done = isDone(task.status);
  const overdue = task.is_overdue && !done;
  const isCall = task.task_type === "call";
  const isMeeting = task.task_type === "meeting";
  // Calls / meetings always carry a precise time via scheduled_at.
  // Plain tasks expose it only when due_at is a full datetime (not
  // a date-only "yyyy-mm-ddT00:00:00").
  const startIso = task.scheduled_at ?? task.due_at!;
  const due = new Date(startIso);
  const time = format(due, "HH:mm");
  const showTime =
    isCall ||
    isMeeting ||
    (startIso.includes("T") &&
      !startIso.endsWith("00:00:00") &&
      due.getHours() + due.getMinutes() > 0);

  const dotClass = overdue ? "bg-rose-500" : PRIORITY_DOT[task.priority];

  // Tooltip surfaces the time as it was scheduled — i.e. in the
  // task's saved timezone. Falls back to nothing when the task has no
  // timezone field or the viewer is already in that zone.
  const taskTz =
    task.scheduled_timezone && task.scheduled_timezone.length > 0
      ? task.scheduled_timezone
      : null;
  const viewerTz = getDefaultTimezone();
  const tzTooltip =
    taskTz && taskTz !== viewerTz
      ? `Scheduled at ${formatTimeInTz(due, taskTz)}`
      : null;
  const titleAttr = tzTooltip
    ? `${task.title} · ${tzTooltip}`
    : task.title;

  if (variant === "card") {
    return (
      <button
        type="button"
        onClick={() => onClick(event)}
        className={cn(
          "group flex w-full items-start gap-2 rounded-lg border bg-white px-2 py-1.5 text-left shadow-sm-tile transition-colors hover:border-indigo-300 hover:bg-indigo-50/40",
          overdue ? "border-rose-200" : "border-slate-200",
          done && "opacity-50",
        )}
      >
        <span
          className={cn("mt-1 h-2 w-2 shrink-0 rounded-full", dotClass)}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div
            className={cn(
              "truncate text-[11px] font-medium",
              overdue ? "text-rose-700" : "text-slate-800",
              done && "line-through",
            )}
          >
            {task.title}
          </div>
          {task.company_name && (
            <div className="mt-0.5 truncate text-[10px] text-slate-500">
              {task.company_name}
            </div>
          )}
          {showTime && (
            <div className="mt-0.5 font-mono text-[10px] tabular-nums text-slate-400">
              {time}
            </div>
          )}
        </div>
      </button>
    );
  }

  // compact (month view) — single-line pill that fits inside the
  // narrow day cell. Call / meeting events get a coloured icon +
  // bold time prefix so they read as scheduled blocks rather than
  // plain todos.
  return (
    <button
      type="button"
      onClick={() => onClick(event)}
      title={titleAttr}
      className={cn(
        "flex w-full items-center gap-1 rounded px-1 py-0.5 text-[11px] transition-colors hover:bg-slate-100",
        done && "opacity-50",
      )}
    >
      {isCall ? (
        <Phone className="h-2.5 w-2.5 shrink-0 text-blue-500" aria-hidden />
      ) : isMeeting ? (
        <Handshake
          className="h-2.5 w-2.5 shrink-0 text-violet-500"
          aria-hidden
        />
      ) : (
        <span
          className={cn("h-1.5 w-1.5 shrink-0 rounded-full", dotClass)}
          aria-hidden
        />
      )}
      {showTime && (
        <span className="shrink-0 font-mono text-[10px] font-semibold tabular-nums text-slate-500">
          {time}
        </span>
      )}
      <span
        className={cn(
          "truncate",
          overdue ? "font-medium text-rose-700" : "text-slate-700",
          done && "line-through",
        )}
      >
        {task.title}
      </span>
    </button>
  );
}
